// src/pages/SearchResultsPage.jsx - SEARCH + CATEGORY BİRLEŞİK SAYFA
import React, { useState, useEffect, useMemo } from 'react';
import { useSearchParams, useNavigate, Link, useParams } from 'react-router-dom';
import {
  Search, Filter, ChevronRight, ChevronDown, X,
  Grid, List, SlidersHorizontal
} from 'lucide-react';
import ProductCard from '../components/ProductCard';
import { useCart } from '../context/CartContext';
import { useWishlist } from '../context/WishlistContext';
import { useSearch } from '../context/SearchContext';
import { openCartSidebar } from '../components/CartSidebar';
import { getAvailableProducts } from '../services/productService';
import { getAllCampaigns } from '../services/discountService';
import { getCategoryInfo } from '../config/categoryConfig';
import Header from '../components/Header';
import Footer from '../components/Footer';
import toast from 'react-hot-toast';

export default function SearchResultsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { categoryName } = useParams(); // URL'den kategori parametresi

  const [products, setProducts] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);
  const [availableCategories, setAvailableCategories] = useState([]);

  const [selectedCategory, setSelectedCategory] = useState(null); // null = henüz dokunulmadı, '' = "Tümü" seçildi
  const [priceRange, setPriceRange] = useState({ min: '', max: '' });
  const [sortBy, setSortBy] = useState('default');
  const [onlyDiscount, setOnlyDiscount] = useState(false);
  const [onlyInStock, setOnlyInStock] = useState(false);

  // 🆕 Aktif arama sorgusu - URL'den gelen
  const [activeSearchQuery, setActiveSearchQuery] = useState('');

  const { cartCount } = useCart();
  const { wishlistCount } = useWishlist();
  const { searchQuery, setSearchQuery } = useSearch();

  // URL'den gelen filtre parametresi
  const filterType = searchParams.get('filter') || '';

  // Kategori bilgisi
  const isCategory = !!categoryName;
  const categoryInfo = isCategory ? getCategoryInfo(categoryName) : null;

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [productsResult, campaignsResult] = await Promise.all([
          getAvailableProducts(),
          getAllCampaigns(),
        ]);

        // ✅ KAMPANYALARI FİLTRELE - SÜRESIZ KAMPANYALARI DA DAHİL ET
        if (campaignsResult.success) {
          const now = new Date();
          
          const activeCampaigns = campaignsResult.campaigns.filter(campaign => {
            // ✅ Sadece discount tipindeki kampanyaları al
            if (campaign.discountType !== 'discount') {
              console.log('⏭️ Kampanya atlandı (discount değil):', campaign.title);
              return false;
            }
            
            // ✅ Süresiz kampanya kontrolü
            if (!campaign.endDate || campaign.endDate === 'null' || campaign.endDate === null) {
              console.log('✅ Süresiz kampanya eklendi:', campaign.title);
              return true;
            }
            
            // ✅ Tarih kontrolü
            const endDate = new Date(campaign.endDate);
            const isActive = endDate > now;
            console.log(`📅 ${campaign.title}: ${isActive ? 'Aktif' : 'Süresi dolmuş'}`);
            return isActive;
          });
          
          console.log('✅ SearchResultsPage - Aktif kampanyalar:', activeCampaigns.length);
          activeCampaigns.forEach(c => {
            console.log(`  - ${c.title}:`);
            console.log(`    Discount: ${c.discountPercent}%`);
            console.log(`    Category: ${c.targetCategory || 'Genel'}`);
            console.log(`    End Date: ${c.endDate || 'Süresiz'}`);
          });
          
          setCampaigns(activeCampaigns);
        }

        if (productsResult.success) {
          const activeProducts = productsResult.products.filter(product =>
            product.isActive !== false
          );
          setProducts(activeProducts);

          const categoriesSet = new Set();
          activeProducts.forEach(product => {
            if (product.category) {
              const mainCat = product.category.includes(' > ')
                ? product.category.split(' > ')[0]
                : product.category;
              categoriesSet.add(mainCat);
            }
          });
          setAvailableCategories(Array.from(categoriesSet));
        }

      } catch (error) {
        console.error('❌ Veri yükleme hatası:', error);
        toast.error('Ürünler yüklenemedi');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  // 🆕 URL değiştiğinde state'leri senkronize et
  useEffect(() => {
    const q = searchParams.get('q');
    if (q) {
      setActiveSearchQuery(q);
      setSearchQuery(q);
    }

    // 🔥 Kategori sayfasına geçildiğinde, sidebar'ı URL kategorisiyle senkronize et
    if (isCategory && categoryName) {
      // Sadece sayfa ilk yüklendiğinde veya kategori değiştiğinde
      // (kullanıcı "Tümü" seçtiyse bunu override etme)
      if (selectedCategory === null || selectedCategory !== '') {
        setSelectedCategory(categoryName);
      }
    } else if (!isCategory && selectedCategory === null) {
      // Arama/filtre sayfasındaysa ve hiç dokunulmadıysa boş string yap
      setSelectedCategory('');
    }
  }, [searchParams, isCategory, categoryName]);

  // Body scroll kilitle
  useEffect(() => {
    if (showFilters) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [showFilters]);

  // Tüm kategorileri topla (hem ana hem alt kategoriler)
  const allCategories = useMemo(() => {
    const cats = new Set();
    products.forEach(p => {
      if (p.category) {
        // Tam kategoriyi ekle
        cats.add(p.category);
      }
    });
    return Array.from(cats).sort();
  }, [products]);

  const filteredProducts = useMemo(() => {
    let filtered = [...products];

    // 🎯 KATEGORİ FİLTRESİ (aynı)
    let activeCategory = '';

    if (selectedCategory !== null) {
      activeCategory = selectedCategory;
    } else if (isCategory && categoryName) {
      activeCategory = categoryName;
    }

    if (activeCategory && activeCategory !== '') {
      filtered = filtered.filter(p => {
        if (!p.category) return false;
        if (p.category === activeCategory) return true;
        const mainCat = p.category.includes(' > ')
          ? p.category.split(' > ')[0]
          : p.category;
        return mainCat === activeCategory;
      });
    }

    // 🎯 FİLTRE TİPİNE GÖRE ÜRÜN SEÇME
    if (filterType === 'campaign') {
      console.log('🏷️ Kampanya filtresi uygulanıyor...');
      console.log('📦 Toplam ürün:', filtered.length);
      console.log('🎯 Aktif kampanya sayısı:', campaigns.length);
      
      // ✅ Süresiz kampanyaları da dahil et
      const activeCampaigns = campaigns.filter(c => {
        if (!c.endDate || c.endDate === 'null' || c.endDate === null) return true;
        return new Date(c.endDate) > new Date();
      });
      
      console.log('✅ Geçerli kampanya sayısı:', activeCampaigns.length);
      
      filtered = filtered.filter(product => {
        const isEligible = activeCampaigns.some(campaign => {
          // Genel kampanya
          if (!campaign.targetCategory || campaign.targetCategory === '') {
            return true;
          }
          
          // Kategori eşleşmesi - ✅ ALT KATEGORİ DESTEĞİ
          const productMainCategory = product.category.includes(' > ')
            ? product.category.split(' > ')[0]
            : product.category;
          
          const campaignMainCategory = campaign.targetCategory.includes(' > ')
            ? campaign.targetCategory.split(' > ')[0]
            : campaign.targetCategory;
          
          return productMainCategory === campaignMainCategory;
        });
        
        if (isEligible) {
          console.log(`  ✅ ${product.name} - Kampanya uygun`);
        }
        
        return isEligible;
      });
      
      console.log('📊 Kampanyalı ürün sayısı:', filtered.length);
      
    } else if (filterType === 'offers') {
      // ✅ Süresiz kampanyaları da dahil et
      const activeCampaigns = campaigns.filter(c => {
        if (!c.endDate || c.endDate === 'null' || c.endDate === null) return true;
        return new Date(c.endDate) > new Date();
      });
      
      const campaignProducts = filtered.filter(product => {
        return activeCampaigns.some(campaign => {
          if (!campaign.targetCategory || campaign.targetCategory === '') return true;
          
          const productMainCategory = product.category.includes(' > ')
            ? product.category.split(' > ')[0]
            : product.category;
          
          const campaignMainCategory = campaign.targetCategory.includes(' > ')
            ? campaign.targetCategory.split(' > ')[0]
            : campaign.targetCategory;
          
          return productMainCategory === campaignMainCategory;
        });
      });

      const uniqueIds = new Set();
      filtered = [...campaignProducts].filter(product => {
        if (uniqueIds.has(product.id)) return false;
        uniqueIds.add(product.id);
        return true;
      });
      
    } else if (filterType === 'discounted') {
      filtered = filtered.filter(p => {
        if (p.discountType && p.discountType !== 'none' && p.discountValue > 0) return true;
        if (p.originalPrice && p.currentPrice && p.originalPrice > p.currentPrice) return true;
        if (p.discount && p.discount > 0) return true;
        return false;
      });
      
    } else if (filterType === 'topCategory') {
      const categoryMap = new Map();

      filtered.forEach(product => {
        if (!product.category) return;

        if (!categoryMap.has(product.category)) {
          categoryMap.set(product.category, []);
        }
        categoryMap.get(product.category).push(product);
      });

      let result = [];

      categoryMap.forEach(categoryProducts => {
        const sorted = [...categoryProducts].sort((a, b) => {
          const scoreA = (a.sales || 0) * 2 + (a.likes || 0);
          const scoreB = (b.sales || 0) * 2 + (b.likes || 0);
          return scoreB - scoreA;
        });

        result.push(...sorted.slice(0, 2));
      });

      filtered = result;
      
    } else if (filterType === 'popular') {
      filtered = filtered
        .filter(p => (p.likes || 0) > 0)
        .sort((a, b) => (b.likes || 0) - (a.likes || 0));
        
    } else if (filterType === 'bestsellers') {
      filtered = filtered
        .filter(p => (p.sales || 0) > 0)
        .sort((a, b) => (b.sales || 0) - (a.sales || 0));
        
    } else if (filterType === 'brand') {
      filtered = filtered
        .filter(p => p.brand && p.brand.toLowerCase() === 'trona')
        .sort((a, b) => {
          const scoreA = (a.sales || 0) * 2 + (a.likes || 0) * 1.5;
          const scoreB = (b.sales || 0) * 2 + (b.likes || 0) * 1.5;
          return scoreB - scoreA;
        });
        
    } else if (filterType === 'new') {
      const twentyDaysAgo = new Date();
      twentyDaysAgo.setDate(twentyDaysAgo.getDate() - 20);

      filtered = filtered
        .filter(product => {
          if (!product.createdAt) return false;

          const createdDate = product.createdAt.toDate
            ? product.createdAt.toDate()
            : new Date(product.createdAt);

          return createdDate >= twentyDaysAgo;
        })
        .sort((a, b) => {
          const dateA = a.createdAt?.toDate
            ? a.createdAt.toDate()
            : new Date(a.createdAt || 0);

          const dateB = b.createdAt?.toDate
            ? b.createdAt.toDate()
            : new Date(b.createdAt || 0);

          return dateB - dateA;
        });
        
    } else if (filterType === 'featured') {
      filtered = filtered.filter(p => p.isFeatured);
    }

    // Arama sorgusu (aynı)
    if (activeSearchQuery.trim()) {
      const q = activeSearchQuery.toLocaleLowerCase('tr').trim();
      filtered = filtered.filter(p =>
        p.name?.toLocaleLowerCase('tr').includes(q) ||
        p.description?.toLocaleLowerCase('tr').includes(q) ||
        p.category?.toLocaleLowerCase('tr').includes(q)
      );
    }

    // Fiyat aralığı (aynı)
    if (priceRange.min) {
      const minPrice = parseFloat(priceRange.min);
      filtered = filtered.filter(p => {
        const productPrice = parseFloat(p.currentPrice || p.price);
        return productPrice >= minPrice;
      });
    }
    if (priceRange.max) {
      const maxPrice = parseFloat(priceRange.max);
      filtered = filtered.filter(p => {
        const productPrice = parseFloat(p.currentPrice || p.price);
        return productPrice <= maxPrice;
      });
    }

    // Sadece indirimli (aynı)
    if (onlyDiscount) {
      filtered = filtered.filter(p => p.discount > 0);
    }

    // Sadece stokta (aynı)
    if (onlyInStock) {
      filtered = filtered.filter(p => p.stock > 0 || p.stock === undefined);
    }

    // Sıralama (aynı)
    switch (sortBy) {
      case 'price-asc': filtered.sort((a, b) => parseFloat(a.price) - parseFloat(b.price)); break;
      case 'price-desc': filtered.sort((a, b) => parseFloat(b.price) - parseFloat(a.price)); break;
      case 'rating': filtered.sort((a, b) => (b.rating || 0) - (a.rating || 0)); break;
      case 'newest': filtered.sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0)); break;
      case 'popular': filtered.sort((a, b) => (b.likes || 0) - (a.likes || 0)); break;
      case 'bestseller': filtered.sort((a, b) => (b.sales || 0) - (a.sales || 0)); break;
      default: break;
    }

    return filtered;
  }, [
    products, 
    campaigns, 
    filterType, 
    activeSearchQuery, 
    selectedCategory, 
    priceRange, 
    sortBy, 
    onlyDiscount, 
    onlyInStock, 
    isCategory, 
    categoryName
  ]);

  const handleSearch = (e) => {
    e.preventDefault();
    // 🆕 Sadece searchQuery doluysa arama yap
    if (searchQuery.trim()) {
      navigate(`/search?q=${encodeURIComponent(searchQuery.trim())}`);
    }
    // searchQuery boşsa hiçbir şey yapma - son sonuçlar korunur
  };

  const clearFilters = () => {
    setSearchQuery('');
    setActiveSearchQuery(''); // 🆕 Aktif aramayı da temizle
    setSelectedCategory('');
    setPriceRange({ min: '', max: '' });
    setSortBy('default');
    setOnlyDiscount(false);
    setOnlyInStock(false);

    if (isCategory) {
      // Kategorideyse sadece kategoriyi koru
      navigate(`/category/${categoryName}`);
    } else if (filterType) {
      // Filter varsa sadece onu koru
      setSearchParams({ filter: filterType });
    } else {
      setSearchParams({});
    }
  };

  const activeFilterCount = [
    activeSearchQuery, selectedCategory, priceRange.min, priceRange.max,
    onlyDiscount, onlyInStock, sortBy !== 'default'
  ].filter(Boolean).length;

  // Başlık belirleme
  const getPageTitle = () => {
    if (isCategory && categoryInfo) {
      return `${categoryInfo.emoji} ${categoryName}`;
    }
    switch (filterType) {
      case 'campaign': return '🏷️ Kampanyalı Ürünler';
      case 'offers': return '🎉 Aktif Fırsatlar';
      case 'discounted': return '⚡ Bugünün Fırsatları';
      case 'popular': return '🔥 Popüler Ürünler';
      case 'bestsellers': return '⭐ En Çok Satanlar';
      case 'new': return '🆕 Yeni Ürünler';
      case 'featured': return '⭐ Öne Çıkan Ürünler';
      case 'topCategory': return '🔥 Kategorilerin En İyileri';
      case 'brand': return '🏅 Trona Ürünleri';
      default: return activeSearchQuery ? `"${activeSearchQuery}" için sonuçlar` : 'Ürünler';
    }
  };

  return (
    <div className="unified-page">
      <Style />

      <Header
        wishlistCount={wishlistCount}
        cartCount={cartCount}
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        onSearch={handleSearch}
        onCartClick={openCartSidebar}
        onWishlistClick={() => navigate('/wishlist')}
        onCampaignsClick={() => navigate('/campaigns')}
        availableCategories={availableCategories}
        products={products}
      />

      <div className="container">
        <div className="breadcrumb">
          <Link to="/">Anasayfa</Link>
          <ChevronRight size={16} />

          {/* Kategori breadcrumb ayrıştırması */}
          {isCategory && categoryName ? (
            <>
              {categoryName.includes(' > ') ? (
                <>
                  {/* Ana Kategori */}
                  <Link to={`/category/${encodeURIComponent(categoryName.split(' > ')[0])}`}>
                    {getCategoryInfo(categoryName.split(' > ')[0]).emoji} {categoryName.split(' > ')[0]}
                  </Link>
                  <ChevronRight size={16} />

                  {/* Alt Kategori */}
                  <span>
                    {getCategoryInfo(categoryName).emoji} {categoryName.split(' > ')[1]}
                  </span>
                </>
              ) : (
                <>
                  {/* Tek seviye kategori */}
                  <span>
                    {getCategoryInfo(categoryName).emoji} {categoryName}
                  </span>
                </>
              )}
            </>
          ) : (
            <span>{getPageTitle()}</span>
          )}
        </div>

        {/* Kategori Hero Banner */}
        {isCategory && categoryInfo && (
          <div className="category-hero" style={{
            background: `linear-gradient(135deg, ${getCategoryColor(categoryName)}20, ${getCategoryColor(categoryName)}10)`
          }}>
            <div className="hero-icon" style={{ background: getCategoryColor(categoryName) }}>
              {categoryInfo.emoji}
            </div>
            <div className="hero-content">
              <h1>{categoryName}</h1>
              <p>{getCategoryDescription(categoryName)}</p>
              <span className="product-count">{filteredProducts.length} ürün</span>
            </div>
          </div>
        )}

        <div className="search-layout">
          {/* Filtreler Sidebar */}
          <aside className={`filters-sidebar ${showFilters ? 'mobile-open' : ''}`}>
            <div className="filters-header-search">
              <h3><Filter size={18} /> Filtreler</h3>
              {activeFilterCount > 0 && (
                <button className="clear-filters" onClick={clearFilters}>
                  Temizle ({activeFilterCount})
                </button>
              )}
              <button className="close-filters" onClick={() => setShowFilters(false)}>
                <X size={24} />
              </button>
            </div>

            <div className="filter-group">
              <h4>Kategori</h4>
              <div className="filter-options">
                <label className={selectedCategory === '' ? 'active' : ''}>
                  <input
                    type="radio"
                    name="category"
                    checked={selectedCategory === ''}
                    onChange={() => {
                      setSelectedCategory('');
                      // ✅ URL'i temizle (arama sayfasına dön)
                      if (filterType) {
                        navigate(`/search?filter=${filterType}`);
                      } else {
                        navigate('/search');
                      }
                    }}
                  />
                  <span>Tümü</span>
                </label>

                {/* Kategorileri hiyerarşik göster */}
                {(() => {
                  // Ana kategorileri grupla
                  const mainCategories = {};

                  allCategories.forEach(fullCat => {
                    if (fullCat.includes(' > ')) {
                      const [mainCat, subCat] = fullCat.split(' > ');
                      if (!mainCategories[mainCat]) {
                        mainCategories[mainCat] = [];
                      }
                      mainCategories[mainCat].push(fullCat);
                    } else {
                      if (!mainCategories[fullCat]) {
                        mainCategories[fullCat] = [];
                      }
                    }
                  });

                  return Object.entries(mainCategories).map(([mainCat, subCats]) => {
                    const mainCatInfo = getCategoryInfo(mainCat);

                    return (
                      <div key={mainCat} className="category-group">
                        {/* Ana Kategori */}
                        <label className={selectedCategory === mainCat ? 'active main-category' : 'main-category'}>
                          <input
                            type="radio"
                            name="category"
                            checked={selectedCategory === mainCat}
                            onChange={() => {
                              setSelectedCategory(mainCat);
                              // ✅ URL'i güncelle - kategori sayfasına git
                              navigate(`/category/${encodeURIComponent(mainCat)}`);
                            }}
                          />
                          <span>{mainCatInfo.emoji} {mainCat}</span>
                        </label>

                        {/* Alt Kategoriler */}
                        {subCats.length > 0 && (
                          <div className="sub-categories">
                            {subCats.map(fullCat => {
                              const subCatInfo = getCategoryInfo(fullCat);

                              return (
                                <label
                                  key={fullCat}
                                  className={selectedCategory === fullCat ? 'active sub-category' : 'sub-category'}
                                >
                                  <input
                                    type="radio"
                                    name="category"
                                    checked={selectedCategory === fullCat}
                                    onChange={() => {
                                      setSelectedCategory(fullCat);
                                      // ✅ URL'i güncelle - alt kategori sayfasına git
                                      navigate(`/category/${encodeURIComponent(fullCat)}`);
                                    }}
                                  />
                                  <span>{subCatInfo.emoji} {fullCat.split(' > ')[1]}</span>
                                </label>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  });
                })()}
              </div>
            </div>

            {/* Fiyat Aralığı */}
            <div className="filter-group">
              <h4>Fiyat Aralığı</h4>
              <div className="price-inputs">
                <input
                  placeholder="Min"
                  value={priceRange.min}
                  onChange={(e) => setPriceRange(prev => ({ ...prev, min: e.target.value }))}
                />
                <span>-</span>
                <input
                  placeholder="Max"
                  value={priceRange.max}
                  onChange={(e) => setPriceRange(prev => ({ ...prev, max: e.target.value }))}
                />
              </div>
            </div>

            {/* Hızlı Filtreler */}
            <div className="filter-group">
              <h4>Hızlı Filtreler</h4>
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={onlyDiscount}
                  onChange={(e) => setOnlyDiscount(e.target.checked)}
                />
                <span>Sadece İndirimli Ürünler</span>
              </label>
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={onlyInStock}
                  onChange={(e) => setOnlyInStock(e.target.checked)}
                />
                <span>Sadece Stokta Olanlar</span>
              </label>
            </div>

            {/* Diğer Kategoriler */}
            {allCategories.length > 0 && (
              <div className="filter-group">
                <h4>Diğer Kategoriler</h4>
                <div className="category-links">
                  {allCategories.slice(0, 8).map(cat => {
                    const catInfo = getCategoryInfo(cat);
                    const isActive = (isCategory && categoryName === cat) || selectedCategory === cat;

                    return (
                      <Link
                        key={cat}
                        to={`/category/${encodeURIComponent(cat)}`}
                        className={isActive ? 'active category-link-btn' : 'category-link-btn'}
                        onClick={() => {
                          setSelectedCategory(cat);
                          navigate(`/category/${encodeURIComponent(cat)}`);
                          setShowFilters(false);
                        }}
                      >
                        <span className="cat-icon">{catInfo.emoji}</span>
                        {cat}
                      </Link>
                    );
                  })}
                </div>
              </div>
            )}

            <button className="apply-filters-btn" onClick={() => setShowFilters(false)}>
              Filtreleri Uygula ({filteredProducts.length} ürün)
            </button>
          </aside>

          {/* Ürünler */}
          <main className="products-main">
            <div className="products-toolbar">
              <button className="filter-toggle" onClick={() => setShowFilters(true)}>
                <SlidersHorizontal size={18} />
                <span>Filtreler</span>
                {activeFilterCount > 0 && <span className="badge">{activeFilterCount}</span>}
              </button>

              <div className="sort-select">
                <select value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
                  <option value="default">Sırala</option>
                  <option value="price-asc">Fiyat ↑</option>
                  <option value="price-desc">Fiyat ↓</option>
                  <option value="rating">Puan</option>
                  <option value="newest">Yeni</option>
                  <option value="popular">Popüler</option>
                  <option value="bestseller">Çok Satan</option>
                </select>
                <ChevronDown size={16} />
              </div>
            </div>

            {loading ? (
              <div className="loading-grid">
                {[...Array(6)].map((_, i) => <div key={i} className="skeleton-card"></div>)}
              </div>
            ) : filteredProducts.length === 0 ? (
              <div className="no-results">
                <Search size={64} strokeWidth={1} />
                <h2>Sonuç Bulunamadı</h2>
                <p>Arama kriterlerinize uygun ürün bulunamadı. Filtreleri değiştirmeyi deneyin.</p>
                <button onClick={clearFilters}>Filtreleri Temizle</button>
              </div>
            ) : (
              <div className={`products-grid`}>
                {filteredProducts.map(product => (
                  <ProductCard
                    key={product.id}
                    product={product}
                    campaigns={campaigns}
                  />
                ))}
              </div>
            )}
          </main>
        </div>
      </div>

      <Footer />
      {showFilters && <div className="filter-overlay" onClick={() => setShowFilters(false)} />}
    </div>
  );
}

// Helper functions
function getCategoryColor(categoryName) {
  const colors = {
    'Gıda & İçecek': '#ff6b6b',
    'Ev & Mutfak': '#4ecdc4',
    'Kişisel Bakım': '#9b59b6',
    'Elektronik': '#3498db',
    'Hobi & Yaşam': '#f39c12',
    'Bebek & Çocuk': '#e91e63',
    'Giyim & Aksesuar': '#2ecc71',
    'Evcil Hayvan': '#8b4513'
  };
  return colors[categoryName] || '#95a5a6';
}

function getCategoryDescription(categoryName) {
  const descriptions = {
    'Gıda & İçecek': 'Taze ve kaliteli gıda ürünleri',
    'Ev & Mutfak': 'Mutfak ve ev ürünleri',
    'Kişisel Bakım': 'Kişisel bakım ürünleri',
    'Elektronik': 'Teknoloji ürünleri',
    'Hobi & Yaşam': 'Hobi ve yaşam ürünleri',
    'Bebek & Çocuk': 'Bebek ve çocuk ürünleri',
    'Giyim & Aksesuar': 'Giyim ve aksesuar ürünleri',
    'Evcil Hayvan': 'Evcil hayvan ürünleri'
  };
  return descriptions[categoryName] || 'Kategorideki tüm ürünler';
}

function Style() {
  return <style>{`
    * { margin: 0; padding: 0; box-sizing: border-box; }
    .unified-page { min-height: 100vh; background: #f8f9fa; padding: 0 0 0px; }
    .container { max-width: 1320px; margin: 0 auto; padding: 0 20px; }
    
    .breadcrumb { display: flex; align-items: center; gap: 8px; margin: 20px 0; font-size: 14px; flex-wrap: wrap; }
    .breadcrumb a { color: #868e96; text-decoration: none; }
    .breadcrumb a:hover { color: #E5332A; }
    
    .category-hero { border-radius: 20px; padding: 40px; display: flex; align-items: center; gap: 24px; margin-bottom: 32px; }
    .hero-icon { width: 80px; height: 80px; border-radius: 20px; display: flex; align-items: center; justify-content: center; font-size: 40px; flex-shrink: 0; }
    .hero-content h1 { font-size: 32px; margin-bottom: 8px; }
    .hero-content p { color: #495057; margin-bottom: 8px; }
    .product-count { font-size: 14px; color: #868e96; }
    
    .search-layout { display: grid; grid-template-columns: 260px 1fr; gap: 32px; }
    
    /* Filters */
    .filters-sidebar { background: #fff; border-radius: 16px; padding: 24px; height: fit-content; position: sticky; top: 20px; margin-bottom: 32px }
    .filters-header-search { display: flex; align-items: center; gap: 12px; margin-bottom: 24px; }
    .filters-header-search h3 { display: flex; align-items: center; gap: 8px; font-size: 18px; flex: 1; margin: 0; }
    .clear-filters { background: none; border: none; color: #E5332A; font-size: 13px; cursor: pointer; }
    .close-filters { display: none; background: none; border: none; cursor: pointer; color: #2B2B2B; }
    .filter-group { margin-bottom: 24px; padding-bottom: 24px; border-bottom: 1px solid #e9ecef; }
    .filter-group:last-of-type { border-bottom: none; }
    .filter-group h4 { font-size: 14px; margin-bottom: 12px; color: #495057; }
    .filter-options { display: flex; flex-direction: column; gap: 6px; max-height: 300px; overflow-y: auto; }
    .filter-options label { display: flex; align-items: center; gap: 10px; padding: 8px 12px; border-radius: 8px; cursor: pointer; font-size: 14px; transition: all 0.2s; }
    .filter-options label:hover { background: #f8f9fa; }
    .filter-options label.active { background: #fff5f5; color: #E5332A; }
    .filter-options input { display: none; }
    
    /* Kategori Grupları */
    .category-group { margin-bottom: 8px; }
    .main-category { font-weight: 600; background: #f8f9fa !important; }
    .main-category:hover { background: #e9ecef !important; }
    .main-category.active { background: #fff5f5 !important; color: #E5332A; font-weight: 700; }
    
    .sub-categories { margin-left: 20px; margin-top: 4px; display: flex; flex-direction: column; gap: 4px; }
    .sub-category { font-size: 13px; padding: 6px 10px; }
    .sub-category span { color: #495057; }
    .sub-category:hover { background: #f8f9fa; }
    .sub-category.active { background: #fff5f5; }
    .sub-category.active span { color: #E5332A; font-weight: 600; }
    .price-inputs { display: flex; align-items: center; gap: 8px; }
    .price-inputs input { width: 100%; padding: 10px; border: 1px solid #e9ecef; border-radius: 8px; font-size: 14px; }
    .checkbox-label { display: flex; align-items: center; gap: 10px; cursor: pointer; font-size: 14px; margin-bottom: 10px; }
    .checkbox-label input { width: 18px; height: 18px; accent-color: #E5332A; }
    .category-links { display: flex; flex-direction: column; gap: 4px; }
    .category-links a { display: flex; align-items: center; gap: 10px; padding: 10px 12px; border-radius: 10px; text-decoration: none; color: #495057; font-size: 14px; }
    .category-links a:hover { background: #f8f9fa; }
    .category-links a.active { background: #fff5f5; color: #E5332A; font-weight: 600; }
    .category-link-btn {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  border-radius: 10px;
  text-decoration: none;
  color: #495057;
  font-size: 14px;
  background: none;
  border: none;
  width: 100%;
  text-align: left;
  cursor: pointer;
  transition: all 0.2s;
}

.category-link-btn:hover {
  background: #f8f9fa;
}

.category-link-btn.active {
  background: #fff5f5;
  color: #E5332A;
  font-weight: 600;
}
    .cat-icon { font-size: 18px; }
    .apply-filters-btn { display: none; }
    
    /* Toolbar */
    .products-toolbar { display: flex; align-items: center; gap: 16px; margin-bottom: 24px; background: #fff; padding: 16px; border-radius: 12px; flex-wrap: wrap; }
    .filter-toggle { display: none; align-items: center; gap: 8px; background: none; border: 1px solid #e9ecef; padding: 10px 16px; border-radius: 10px; font-weight: 500; cursor: pointer; position: relative; }
    .filter-toggle .badge { position: absolute; top: -6px; right: -6px; background: #E5332A; color: #fff; font-size: 13px; width: 20px; height: 20px; border-radius: 50%; display: flex; align-items: center; justify-content: center; }
    .sort-select { position: relative; margin-left: auto; }
    .sort-select select { appearance: none; background: #f8f9fa; border: none; padding: 10px 36px 10px 14px; border-radius: 10px; font-size: 14px; cursor: pointer; }
    .sort-select svg { position: absolute; right: 10px; top: 50%; transform: translateY(-50%); pointer-events: none; color: #868e96; }
    .view-toggle { display: flex; border: 1px solid #e9ecef; border-radius: 10px; overflow: hidden; }
    .view-toggle button { background: none; border: none; padding: 10px 12px; cursor: pointer; color: #868e96; }
    .view-toggle button.active { background: #E5332A; color: #fff; }
    
    /* Products Grid - HomePage ile aynı */
    .products-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(220px, 1fr)); gap: 20px; margin-bottom: 32px; }
    .products-grid.list { grid-template-columns: 1fr; }
    
    /* Product Card - HomePage ile TAMAMEN aynı */
    .product-card { 
      background: #fff; 
      border-radius: 12px; 
      overflow: hidden; 
      position: relative; 
      cursor: pointer; 
      box-shadow: 0 2px 8px rgba(0,0,0,0.04); 
      transition: all 0.3s; 
      height: 380px; 
      width: 100%;
      max-width: 260px;
      margin: 0 auto;
      display: flex; 
      flex-direction: column; 
    }
    .product-card:hover { transform: translateY(-4px); box-shadow: 0 8px 24px rgba(0,0,0,0.1); }
    .discount-badge { position: absolute; top: 8px; left: 8px; background: #E5332A; color: #fff; padding: 4px 10px; border-radius: 6px; font-size: 11px; font-weight: 700; z-index: 2; display: flex; align-items: center; gap: 3px; }
    .campaign-indicator { font-size: 10px; animation: bounce 1s infinite; }
    @keyframes bounce { 0%, 100% { transform: scale(1); } 50% { transform: scale(1.2); } }
    .wishlist-btn { position: absolute; top: 8px; right: 8px; width: 32px; height: 32px; background: rgba(255,255,255,0.9); border: none; border-radius: 50%; cursor: pointer; display: flex; align-items: center; justify-content: center; z-index: 2; transition: all 0.2s; }
    .wishlist-btn:hover { background: #fff; transform: scale(1.1); }
    .product-image { width: 100%; aspect-ratio: 1; overflow: hidden; background: #f8f9fa; }
    .product-image img { width: 100%; height: 100%; object-fit: contain; transition: transform 0.4s; }
    .product-card:hover .product-image img { transform: scale(1.08); }
    .image-skeleton { width: 100%; height: 100%; background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%); background-size: 200% 100%; animation: loading 1.5s infinite; }
    @keyframes loading { 0% { background-position: 200% 0; } 100% { background-position: -200% 0; } }
    .product-info { padding: 12px; display: flex; flex-direction: column; flex: 1; }
    .product-brand { color: #868e96; font-size: 12px; font-weight: 600; text-transform: uppercase; margin-bottom: 4px; }
    .product-desc { color: #2B2B2B; font-size: 0.8rem; margin-bottom: 0.75rem; line-height: 1.4; display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden; min-height: 2.2em; }
    .product-rating { display: flex; align-items: center; gap: 4px; font-size: 11px; margin-bottom: 10px; }
    .reviews { color: #868e96; }
    .product-footer { display: flex; justify-content: space-between; align-items: center; gap: 8px; margin-top: auto; }
    .product-price { display: flex; flex-direction: column; gap: 2px; flex: 1; }
    .old-price { color: #868e96; font-size: 11px; text-decoration: line-through; }
    .current-price { color: #E5332A; font-size: 16px; font-weight: 800; }
    .campaign-label { color: #E5332A; font-size: 8px; font-weight: 700; background: rgba(229, 51, 42, 0.1); padding: 2px 6px; border-radius: 3px; display: inline-block; margin-top: 2px; }
    .add-cart-btn { width: 36px; height: 36px; background: #E5332A; border: none; border-radius: 8px; color: #fff; display: flex; align-items: center; justify-content: center; cursor: pointer; transition: all 0.2s; flex-shrink: 0; }
    .add-cart-btn:hover { background: #c72a22; transform: scale(1.1); }
    .add-cart-btn.in-cart { background: #06d6a0; }
    .add-cart-btn.adding { animation: pulse-btn 0.3s; }
    @keyframes pulse-btn { 50% { transform: scale(1.2); } }
    
    /* List View Card Styles */
    .product-card-list { display: flex; align-items: center; gap: 20px; background: #fff; border-radius: 16px; padding: 20px; cursor: pointer; transition: all 0.2s; }
    .product-card-list:hover { box-shadow: 0 4px 12px rgba(0,0,0,0.08); }
    .product-card-list .card-image { width: 120px; height: 120px; border-radius: 12px; overflow: hidden; flex-shrink: 0; position: relative; background: #f8f9fa; }
    .product-card-list .card-image img { width: 100%; height: 100%; object-fit: cover; }
    .product-card-list .discount-badge { position: absolute; top: 8px; left: 8px; }
    .product-card-list .card-content { flex: 1; min-width: 0; }
    .product-card-list h3 { font-size: 16px; margin-bottom: 8px; }
    .product-card-list p { font-size: 13px; color: #868e96; margin-bottom: 8px; display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden; }
    .product-card-list .rating { display: flex; align-items: center; gap: 4px; font-size: 13px; }
    .product-card-list .card-actions { text-align: right; flex-shrink: 0; }
    .product-card-list .price { display: flex; flex-direction: column; gap: 4px; align-items: flex-end; margin-bottom: 12px; }
    .product-card-list .price .old { font-size: 13px; color: #868e96; text-decoration: line-through; }
    .product-card-list .price .current { font-size: 20px; font-weight: 700; color: #E5332A; }
    .product-card-list .buttons { display: flex; gap: 8px; justify-content: flex-end; }
    .product-card-list .cart-btn, .product-card-list .wish-btn { width: 44px; height: 44px; border: none; border-radius: 10px; cursor: pointer; display: flex; align-items: center; justify-content: center; }
    .product-card-list .cart-btn { background: #E5332A; color: #fff; }
    .product-card-list .cart-btn.in-cart { background: #06d6a0; }
    .product-card-list .wish-btn { background: #f8f9fa; color: #868e96; }
    .product-card-list .wish-btn.active { color: #E5332A; }
    
    .loading-grid { display: grid; grid-template-columns: repeat(5, 1fr); gap: 20px; }
    .skeleton-card { height: 380px; background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%); background-size: 200% 100%; animation: loading 1.5s infinite; border-radius: 12px; }
    @keyframes loading { 0% { background-position: 200% 0; } 100% { background-position: -200% 0; } }
    
    .no-results { text-align: center; padding: 60px 20px; background: #fff; border-radius: 16px; }
    .no-results svg { color: #868e96; margin-bottom: 24px; }
    .no-results h2 { margin-bottom: 12px; font-size: 20px; }
    .no-results p { color: #868e96; margin-bottom: 24px; font-size: 14px; }
    .no-results button { background: #E5332A; color: #fff; border: none; padding: 12px 24px; border-radius: 10px; font-weight: 600; cursor: pointer; }
    
    .filter-overlay { position: fixed; top: 0; left: 0; right: 0; bottom: 0; background: rgba(0,0,0,0.5); z-index: 100; }

    @media (max-width: 1200px) {
      .products-grid { grid-template-columns: repeat(auto-fill, minmax(200px, 1fr)); gap: 18px; }
      .loading-grid { grid-template-columns: repeat(4, 1fr); }
    }

    @media (max-width: 992px) {
  .search-layout { grid-template-columns: 1fr; }
  .filters-sidebar {
    position: fixed;
    top: 0;
    left: 0;
    width: 300px;
    max-width: 90%; /* Daha esnek */
    height: 100%;
    border-radius: 0;
    transform: translateX(-100%);
    transition: transform 0.3s;
    z-index: 1001;
    overflow-y: auto;
    padding-top: 70px; /* Header yüksekliği */
    padding-bottom: 100px;
  }
  .filters-sidebar.mobile-open { transform: translateX(0); }
  
  .filters-header-search {
    position: fixed;
    top: 0;
    left: 0;
    width: 300px;
    max-width: 90%;
    background: #fff;
    padding: 15px 20px;
    border-bottom: 1px solid #e9ecef;
    z-index: 1002;
    display: flex;
    align-items: center;
    justify-content: space-between;
  }
  
  .close-filters { 
    display: block;
    background: none;
    border: none;
    cursor: pointer;
    padding: 8px;
    margin: -8px;
  }
  
  .filter-toggle { 
    display: flex;
    align-items: center;
    gap: 8px;
    background: none;
    border: 1px solid #e9ecef;
    padding: 10px 16px;
    border-radius: 10px;
    font-weight: 500;
    cursor: pointer;
    position: relative;
  }
  
  .filter-group:first-of-type {
    margin-top: 10px;
  }
  
  .products-grid { grid-template-columns: repeat(auto-fill, minmax(180px, 1fr)); }
  .loading-grid { grid-template-columns: repeat(4, 1fr); }
}

/* Filter overlay için güncelleme */
.filter-overlay { 
  position: fixed; 
  top: 0; 
  left: 0; 
  right: 0; 
  bottom: 0; 
  background: rgba(0,0,0,0.5); 
  z-index: 1000; /* Header ile aynı seviyede */
  backdrop-filter: blur(2px);
}

    @media (max-width: 768px) {
      .unified-page { padding: 0 0 40px; }
      .container { padding: 0 16px; }
      .breadcrumb { font-size: 13px; margin-bottom: 16px; }
      .category-hero { padding: 20px; gap: 16px; margin-bottom: 20px; border-radius: 16px; }
      .hero-icon { width: 56px; height: 56px; font-size: 28px; border-radius: 14px; }
      .hero-content h1 { font-size: 20px; }
      .hero-content p { font-size: 13px; }
      .products-toolbar { padding: 12px; gap: 8px; }
      .filter-toggle { padding: 8px 12px; font-size: 13px; }
      .filter-toggle span { display: none; }
      .sort-select { margin-left: 0; flex: 1; }
      .sort-select select { width: 100%; padding: 8px 30px 8px 10px; font-size: 13px; }
      .products-grid { grid-template-columns: repeat(2, 1fr); gap: 12px; }
      .product-card { max-width: 100%; height: 320px; }
      .product-card .product-info { padding: 10px; }
      .product-card .product-brand { font-size: 10px; }
      .product-card .product-desc { font-size: 0.75rem; }
      .product-card .product-rating { font-size: 10px; margin-bottom: 8px; }
      .product-card .old-price { font-size: 10px; }
      .product-card .current-price { font-size: 14px; }
      .product-card .campaign-label { font-size: 7px; padding: 2px 4px; }
      .product-card .add-cart-btn { width: 32px; height: 32px; border-radius: 6px; }
      .product-card .discount-badge { padding: 3px 8px; font-size: 10px; }
      .product-card .wishlist-btn { width: 28px; height: 28px; }
      .loading-grid { grid-template-columns: repeat(2, 1fr); gap: 12px; }
      .skeleton-card { height: 320px; }
      .product-card-list { 
        flex-wrap: wrap;
        padding: 12px; 
        gap: 12px; 
      }
      .product-card-list .card-image { 
        width: 100px; 
        height: 100px; 
      }
      .product-card-list .card-actions { 
        width: 100%;
        display: flex; 
        justify-content: space-between; 
        align-items: center; 
        padding-top: 12px;
        border-top: 1px solid #f0f0f0;
      }
      .product-card-list .price { 
        align-items: flex-start;
        margin-bottom: 0;
      }
      .product-card-list .price .current { 
        font-size: 16px; 
      }
      .product-card-list .buttons { 
        gap: 6px; 
      }
      .product-card-list .cart-btn, 
      .product-card-list .wish-btn { 
        width: 36px; 
        height: 36px; 
      }
      .no-results { padding: 40px 16px; }

      .filters-sidebar {
    width: 280px;
    max-width: 85%;
    padding-top: 65px;
  }
  
  .filters-header-search {
    width: 280px;
    max-width: 85%;
    padding: 12px 16px;
  }
  
  .filters-header-search h3 {
    font-size: 16px;
  }
  
  /* Filtre toggle butonu daha belirgin */
  .filter-toggle {
    padding: 8px 14px;
    font-size: 14px;
    background: #f8f9fa;
  }
  
  .filter-toggle span {
    display: inline !important;
  }
  
  /* Filtre içerik düzeni */
  .filter-group {
    padding: 0 16px 20px 16px;
    margin-bottom: 20px;
  }
  
  .filter-group h4 {
    font-size: 15px;
    margin-bottom: 12px;
  }
  
  .filter-options {
    max-height: 250px;
  }
  
  .filter-options label {
    padding: 10px;
    font-size: 14px;
  }
  
  /* Kategori grupları */
  .sub-categories {
    margin-left: 15px;
  }
  
  .category-links a {
    padding: 8px 10px;
    font-size: 13px;
  }
  
  /* Breadcrumb ve hero düzeni */
  .breadcrumb { 
    font-size: 13px; 
    margin: 16px 0; 
  }
  
  .category-hero { 
    padding: 16px; 
    gap: 12px; 
    margin-bottom: 20px; 
  }
  
  .hero-icon { 
    width: 50px; 
    height: 50px; 
    font-size: 24px; 
    border-radius: 12px; 
  }
  
  .hero-content h1 { 
    font-size: 18px; 
    margin-bottom: 4px; 
  }
  
  .hero-content p { 
    font-size: 12px; 
  }
  
  /* Ürün toolbar */
  .products-toolbar { 
    padding: 12px; 
    margin-bottom: 20px;
    gap: 12px;
  }
  
  .sort-select {
    flex: 1;
  }
  
  .sort-select select {
    width: 100%;
    padding: 10px 14px;
    font-size: 13px;
  }
    }

    @media (max-width: 480px) {
      .container { padding: 0 12px; }
      .products-toolbar { padding: 10px; }
      .products-grid { gap: 8px; margin-bottom: 20px; }
      .product-card { height: 300px; }
      .product-card .product-info { padding: 8px; }
      .product-card .current-price { font-size: 13px; }
      .product-card .add-cart-btn { width: 28px; height: 28px; }
      .product-card .wishlist-btn { width: 26px; height: 26px; }
    }
  `}</style>;
}